/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import * as signalR from "@microsoft/signalr";
import { useAuth0 } from "@auth0/auth0-react";
import { API_BASE_URL } from "../../services/common/constants";
import { logger } from "../../utils/logger";

interface SignalRConnection {
  connection: signalR.HubConnection;
  isConnected: boolean;
  error: Error | null;
}

interface SignalRContextValue {
  getConnection: (hubUrl: string) => SignalRConnection | undefined;
  joinGroup: (hubUrl: string, groupName: string) => Promise<void>;
  leaveGroup: (hubUrl: string, groupName: string) => Promise<void>;
  onReconnected: (hubUrl: string, callback: () => void) => () => void;
}

const SignalRContext = createContext<SignalRContextValue | undefined>(
  undefined,
);

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }
  return context;
};

interface SignalRProviderProps {
  children: React.ReactNode;
}

// Circuit breaker state for connection retries
interface RetryState {
  retryCount: number;
  nextRetryTime: number;
  isCircuitOpen: boolean;
}

const MAX_RETRY_COUNT = 5;
const INITIAL_RETRY_DELAY_MS = 2000;
const MAX_RETRY_DELAY_MS = 60000;
const CIRCUIT_RESET_TIME_MS = 120000; // 2 minutes

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  children,
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [connections, setConnections] = useState<
    Map<string, SignalRConnection>
  >(new Map());
  const connectionsRef = useRef<Map<string, signalR.HubConnection>>(new Map());
  // Track connections that are currently being started to prevent duplicates
  const connectingRef = useRef<Set<string>>(new Set());
  // Store getAccessTokenSilently in a ref to avoid dependency changes
  const getAccessTokenRef = useRef(getAccessTokenSilently);
  // Track if we've already initialized connections
  const hasInitializedRef = useRef(false);
  // Track reconnection callbacks per hub URL
  const reconnectCallbacksRef = useRef<Map<string, Set<() => void>>>(new Map());
  // Track retry state per hub URL (circuit breaker pattern)
  const retryStateRef = useRef<Map<string, RetryState>>(new Map());
  // Track scheduled retry timeouts
  const retryTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  // Track if we're in a reconnection state (to force fresh token)
  const isReconnectingRef = useRef(false);
  // Ref for scheduleRetry to avoid stale closure in createConnection
  const scheduleRetryRef = useRef<(hubUrl: string) => void>(() => {});

  // Keep the ref updated
  useEffect(() => {
    getAccessTokenRef.current = getAccessTokenSilently;
  }, [getAccessTokenSilently]);

  const createConnection = useCallback(
    (hubUrl: string): signalR.HubConnection => {
      const normalizedUrl = hubUrl.toLowerCase();

      // Check if connection already exists
      const existingConnection = connectionsRef.current.get(normalizedUrl);
      if (existingConnection) {
        return existingConnection;
      }

      // Create new connection - use ref for getAccessTokenSilently to avoid dependency changes
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${API_BASE_URL}${normalizedUrl}`, {
          accessTokenFactory: async () => {
            try {
              // Force fresh token on reconnect to prevent stale token issues
              const options = isReconnectingRef.current
                ? { cacheMode: 'off' as const }
                : undefined;
              const token = await getAccessTokenRef.current(options);

              // Diagnostic logging to investigate identity mismatch bug
              if (token) {
                try {
                  const payload = JSON.parse(atob(token.split('.')[1]));
                  const tokenEmail = payload.email || payload['https://hotelsuite.example.com/email'];
                  const issuedAt = payload.iat ? new Date(payload.iat * 1000).toISOString() : 'unknown';
                  const expiresAt = payload.exp ? new Date(payload.exp * 1000).toISOString() : 'unknown';
                  logger.log(`SignalR accessTokenFactory (${normalizedUrl}) - email: ${tokenEmail}, sub: ${payload.sub}, iat: ${issuedAt}, exp: ${expiresAt}, aud: ${payload.aud}, isReconnecting: ${isReconnectingRef.current}`);
                } catch {
                  // Ignore JWT decode errors
                }
              }

              return token;
            } catch (error) {
              logger.error("Failed to get access token for SignalR:", error);
              return "";
            }
          }
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff with max of 30 seconds
            const delay = Math.min(5000 * Math.pow(2, retryContext.previousRetryCount), 30000);
            return delay;
          },
        })
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      // Store connection
      connectionsRef.current.set(normalizedUrl, connection);

      // Set up connection state management
      connection.onreconnecting((error) => {
        logger.log(`SignalR ${normalizedUrl} - Reconnecting:`, error);
        isReconnectingRef.current = true; // Force fresh token on next accessTokenFactory call
        updateConnectionState(normalizedUrl, false, error ?? null);
      });

      connection.onreconnected((connectionId) => {
        logger.log(`SignalR ${normalizedUrl} - Reconnected:`, connectionId);
        isReconnectingRef.current = false; // Reset reconnecting flag
        updateConnectionState(normalizedUrl, true, null);
        // Fire all reconnection callbacks for this hub
        const callbacks = reconnectCallbacksRef.current.get(normalizedUrl);
        if (callbacks) {
          callbacks.forEach((cb) => {
            try {
              cb();
            } catch (error) {
              logger.error(`SignalR ${normalizedUrl} - Reconnect callback error:`, error);
            }
          });
        }
      });

      connection.onclose((error) => {
        logger.log(`SignalR ${normalizedUrl} - Closed:`, error);
        updateConnectionState(normalizedUrl, false, error ?? null);
        // Remove from connecting set if it was there
        connectingRef.current.delete(normalizedUrl);
        // Remove dead connection so a fresh one can be created on retry
        connectionsRef.current.delete(normalizedUrl);
        // Schedule reconnection attempt (server may have restarted)
        scheduleRetryRef.current(normalizedUrl);
      });

      return connection;
    },
    [], // No dependencies - use refs instead
  );

  const updateConnectionState = (
    hubUrl: string,
    isConnected: boolean,
    error: Error | null,
  ) => {
    setConnections((prev) => {
      const newMap = new Map(prev);
      const connection = connectionsRef.current.get(hubUrl);
      if (connection) {
        newMap.set(hubUrl, { connection, isConnected, error });
      }
      return newMap;
    });
  };

  // Get or initialize retry state for a hub
  const getRetryState = useCallback((hubUrl: string): RetryState => {
    if (!retryStateRef.current.has(hubUrl)) {
      retryStateRef.current.set(hubUrl, {
        retryCount: 0,
        nextRetryTime: 0,
        isCircuitOpen: false,
      });
    }
    return retryStateRef.current.get(hubUrl)!;
  }, []);

  // Calculate next retry delay with exponential backoff
  const calculateRetryDelay = useCallback((retryCount: number): number => {
    const delay = Math.min(
      INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount),
      MAX_RETRY_DELAY_MS
    );
    // Add jitter (±20%) to prevent thundering herd
    const jitter = delay * 0.2 * (Math.random() - 0.5);
    return Math.round(delay + jitter);
  }, []);

  // Schedule a retry with exponential backoff
  const scheduleRetry = useCallback((hubUrl: string) => {
    const state = getRetryState(hubUrl);

    // Check circuit breaker
    if (state.isCircuitOpen) {
      const now = Date.now();
      if (now < state.nextRetryTime) {
        logger.log(`SignalR ${hubUrl} - Circuit open, next retry in ${Math.round((state.nextRetryTime - now) / 1000)}s`);
        return;
      }
      // Circuit timeout expired, allow one retry
      logger.log(`SignalR ${hubUrl} - Circuit timeout expired, attempting reconnect`);
    }

    // Check if max retries exceeded
    if (state.retryCount >= MAX_RETRY_COUNT) {
      // Open circuit breaker
      state.isCircuitOpen = true;
      state.nextRetryTime = Date.now() + CIRCUIT_RESET_TIME_MS;
      state.retryCount = 0;
      logger.log(`SignalR ${hubUrl} - Max retries (${MAX_RETRY_COUNT}) exceeded, circuit open for ${CIRCUIT_RESET_TIME_MS / 1000}s`);
      return;
    }

    // Clear any existing timeout
    const existingTimeout = retryTimeoutsRef.current.get(hubUrl);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const delay = calculateRetryDelay(state.retryCount);
    logger.log(`SignalR ${hubUrl} - Scheduling retry #${state.retryCount + 1} in ${delay}ms`);

    const timeout = setTimeout(() => {
      retryTimeoutsRef.current.delete(hubUrl);
      state.retryCount++;
      startConnection(hubUrl);
    }, delay);

    retryTimeoutsRef.current.set(hubUrl, timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startConnection is intentionally omitted to prevent reconnection loops between scheduleRetry and startConnection
  }, [getRetryState, calculateRetryDelay]);

  // Keep ref in sync for use in createConnection closure
  scheduleRetryRef.current = scheduleRetry;

  // Reset retry state on successful connection
  const resetRetryState = useCallback((hubUrl: string) => {
    const state = retryStateRef.current.get(hubUrl);
    if (state) {
      state.retryCount = 0;
      state.isCircuitOpen = false;
      state.nextRetryTime = 0;
    }
    const timeout = retryTimeoutsRef.current.get(hubUrl);
    if (timeout) {
      clearTimeout(timeout);
      retryTimeoutsRef.current.delete(hubUrl);
    }
  }, []);

  const startConnection = useCallback(
    async (hubUrl: string): Promise<void> => {
      const normalizedUrl = hubUrl.toLowerCase();

      // Check if already connecting to prevent duplicate attempts
      if (connectingRef.current.has(normalizedUrl)) {
        logger.log(`SignalR ${normalizedUrl} - Already connecting, skipping duplicate attempt`);
        return;
      }

      // Check circuit breaker before attempting
      const retryState = getRetryState(normalizedUrl);
      if (retryState.isCircuitOpen) {
        const now = Date.now();
        if (now < retryState.nextRetryTime) {
          logger.log(`SignalR ${normalizedUrl} - Circuit open, skipping connection attempt`);
          return;
        }
      }

      const existingConnection = connectionsRef.current.get(normalizedUrl);

      // Check if already connected or connecting
      if (existingConnection) {
        const state = existingConnection.state;
        if (state === signalR.HubConnectionState.Connected) {
          logger.log(`SignalR ${normalizedUrl} - Already connected`);
          resetRetryState(normalizedUrl);
          return;
        }
        if (state === signalR.HubConnectionState.Connecting ||
            state === signalR.HubConnectionState.Reconnecting) {
          logger.log(`SignalR ${normalizedUrl} - Connection in progress (state: ${state})`);
          return;
        }
      }

      // Mark as connecting
      connectingRef.current.add(normalizedUrl);

      try {
        const connection = createConnection(normalizedUrl);

        if (connection.state === signalR.HubConnectionState.Disconnected) {
          await connection.start();
          logger.log(`SignalR ${normalizedUrl} - Connected successfully`);
          updateConnectionState(normalizedUrl, true, null);
          // Reset retry state on success
          resetRetryState(normalizedUrl);
        }
      } catch (error) {
        logger.error(`SignalR ${normalizedUrl} - Connection failed:`, error);
        updateConnectionState(normalizedUrl, false, error as Error);
        // Schedule retry with exponential backoff
        scheduleRetry(normalizedUrl);
      } finally {
        // Remove from connecting set
        connectingRef.current.delete(normalizedUrl);
      }
    },
    [createConnection, getRetryState, resetRetryState, scheduleRetry],
  );

  const getConnection = useCallback(
    (hubUrl: string): SignalRConnection | undefined => {
      const normalizedUrl = hubUrl.toLowerCase();
      return connections.get(normalizedUrl);
    },
    [connections],
  );

  const joinGroup = useCallback(
    async (hubUrl: string, groupName: string): Promise<void> => {
      const normalizedUrl = hubUrl.toLowerCase();

      // Use ref to check connection state to avoid dependency on connections state
      // This prevents infinite loops when connection state changes
      const existingConnection = connectionsRef.current.get(normalizedUrl);
      const isConnected = existingConnection?.state === signalR.HubConnectionState.Connected;

      if (!isConnected) {
        logger.log(`SignalR ${normalizedUrl} - Not connected, attempting connection before joining group ${groupName}`);
        await startConnection(normalizedUrl);
      }

      let connection = connectionsRef.current.get(normalizedUrl);
      for (let attempt = 0; attempt < 100; attempt += 1) {
        if (connection?.state === signalR.HubConnectionState.Connected) {
          break;
        }
        if (
          connection?.state !== signalR.HubConnectionState.Connecting &&
          connection?.state !== signalR.HubConnectionState.Reconnecting
        ) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
        connection = connectionsRef.current.get(normalizedUrl);
      }

      if (
        connection &&
        connection.state === signalR.HubConnectionState.Connected
      ) {
        try {
          logger.log(
            `SignalR ${normalizedUrl} - Invoking JoinHotelGroup for: ${groupName}`,
          );
          await connection.invoke("JoinHotelGroup", groupName);
          logger.log(
            `SignalR ${normalizedUrl} - Successfully joined group: ${groupName}`,
          );
        } catch (error) {
          logger.error(
            `SignalR ${normalizedUrl} - Failed to join group ${groupName}:`,
            error,
          );
          throw error;
        }
      } else {
        // Log warning when we can't join because connection isn't available
        const state = connection?.state ?? "no connection";
        logger.error(
          `SignalR ${normalizedUrl} - Cannot join group ${groupName}: connection not ready (state: ${state}). Real-time notifications will not work.`,
        );
      }
    },
    [startConnection], // Removed connections dependency to prevent loop
  );

  const leaveGroup = useCallback(
    async (hubUrl: string, groupName: string): Promise<void> => {
      const normalizedUrl = hubUrl.toLowerCase();
      const connection = connectionsRef.current.get(normalizedUrl);

      if (
        connection &&
        connection.state === signalR.HubConnectionState.Connected
      ) {
        try {
          await connection.invoke("LeaveHotelGroup", groupName);
          logger.log(`SignalR ${normalizedUrl} - Left group: ${groupName}`);
        } catch (error) {
          logger.error(
            `SignalR ${normalizedUrl} - Failed to leave group ${groupName}:`,
            error,
          );
        }
      }
    },
    [],
  );

  const onReconnected = useCallback(
    (hubUrl: string, callback: () => void): (() => void) => {
      const normalizedUrl = hubUrl.toLowerCase();
      if (!reconnectCallbacksRef.current.has(normalizedUrl)) {
        reconnectCallbacksRef.current.set(normalizedUrl, new Set());
      }
      reconnectCallbacksRef.current.get(normalizedUrl)!.add(callback);
      logger.log(`SignalR ${normalizedUrl} - Registered reconnect callback`);

      // Return unsubscribe function
      return () => {
        reconnectCallbacksRef.current.get(normalizedUrl)?.delete(callback);
        logger.log(`SignalR ${normalizedUrl} - Unregistered reconnect callback`);
      };
    },
    [],
  );

  // Initialize connections on mount - use separate effect to avoid dependency loop
  useEffect(() => {
    // Only initialize once when authenticated
    if (!isAuthenticated || hasInitializedRef.current) return;

    hasInitializedRef.current = true;
    logger.log("SignalRProvider: Initializing connections for authenticated user");

    // Start connections for both hubs
    const hubs = ["/hub"];

    hubs.forEach((hub) => {
      startConnection(hub);
    });
  }, [isAuthenticated, startConnection]);

  // Reset initialization flag when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      hasInitializedRef.current = false;
    }
  }, [isAuthenticated]);

  // Cleanup on unmount
  useEffect(() => {
    const retryTimeouts = retryTimeoutsRef.current;
    const retryState = retryStateRef.current;
    const currentConnections = connectionsRef.current;
    const connecting = connectingRef.current;

    return () => {
      // Clear all retry timeouts
      retryTimeouts.forEach((timeout) => {
        clearTimeout(timeout);
      });
      retryTimeouts.clear();
      retryState.clear();

      const connectionsCopy = new Map(currentConnections);

      connectionsCopy.forEach((connection, hubUrl) => {
        if (connection.state === signalR.HubConnectionState.Connected ||
            connection.state === signalR.HubConnectionState.Connecting) {
          connection.stop().catch((error) => {
            logger.error(`Error stopping ${hubUrl} connection:`, error);
          });
        }
      });
      currentConnections.clear();
      connecting.clear();
    };
  }, []);

  const contextValue: SignalRContextValue = {
    getConnection,
    joinGroup,
    leaveGroup,
    onReconnected,
  };

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
};
